import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { AxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { JobFinancialSummary } from './JobFinancialSummary';
import { updateJob } from '../api/jobs-api';
import { parseValidationErrors, type ValidationErrors } from '@/lib/utils';
import type { JobDetailDto, JobFormData } from '../types';

interface JobFormProps {
  job: JobDetailDto;
  onSuccess: () => void;
}

export function JobForm({ job, onSuccess }: JobFormProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<JobFormData>({
    description: job.description || '',
    notes: job.notes || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null);

  // Update form data when job changes
  useEffect(() => {
    setFormData({
      description: job.description || '',
      notes: job.notes || '',
    });
  }, [job]);

  const handleChange = (field: keyof JobFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors(null);
    setIsSubmitting(true);

    try {
      await updateJob(job.id, formData);
      onSuccess();
    } catch (err) {
      const parsedErrors = parseValidationErrors(err as AxiosError);
      setValidationErrors(parsedErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewLead = () => {
    navigate(`/leads/${job.leadId}`);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-1">
      {validationErrors && (validationErrors.generalErrors.length > 0 || Object.keys(validationErrors.fieldErrors).length > 0) && (
        <div className="rounded-[20px] bg-red-50 p-2 border border-red-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {Object.keys(validationErrors.fieldErrors).length > 0
                  ? 'Please correct the following errors:'
                  : 'Error:'}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {validationErrors.generalErrors.length > 0 && (
                  <ul className="list-disc pl-5 space-y-1">
                    {validationErrors.generalErrors.map((error, index) => (
                      <li key={`general-${index}`}>{error}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Info Row */}
      <div className="bg-[#F2F2F2] p-1.5 rounded-lg">
        <div className="grid grid-cols-4 gap-1.5">
          <div className="space-y-0">
            <Label htmlFor="jobNumber" className="text-xs">Job Number</Label>
            <Input id="jobNumber" value={job.jobNumber} disabled className="h-7 text-sm" />
          </div>

          <div className="space-y-0">
            <Label htmlFor="status" className="text-xs">Status</Label>
            <Input id="status" value={job.status} disabled className="h-7 text-sm" />
          </div>

          <div className="space-y-0">
            <Label htmlFor="startDate" className="text-xs">Start Date</Label>
            <Input
              id="startDate"
              value={new Date(job.startDate).toLocaleDateString()}
              disabled
              className="h-7 text-sm"
            />
          </div>

          {job.closedDate && (
            <div className="space-y-0">
              <Label htmlFor="closedDate" className="text-xs">Closed Date</Label>
              <Input
                id="closedDate"
                value={new Date(job.closedDate).toLocaleDateString()}
                disabled
                className="h-7 text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Lead Information Section */}
      <div className="bg-white p-1.5 rounded-[20px] border border-[#d9d9d9]">
        <div className="flex items-center justify-between mb-0.5">
          <h3 className="text-[#0A0A9E] font-semibold text-sm">Lead Information</h3>
          <Button
            type="button"
            onClick={handleViewLead}
            variant="ghost"
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-[#0A0A9E] hover:bg-gray-50"
          >
            View Lead
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5L16 12L9 19" stroke="#0A0A9E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Button>
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          <div className="space-y-0">
            <Label className="text-xs">Customer Name</Label>
            <div className="px-2 py-1.5 bg-gray-50 rounded-md text-sm text-gray-700">
              {job.lead.customerName}
            </div>
          </div>

          <div className="space-y-0">
            <Label className="text-xs">Property Address</Label>
            <div className="px-2 py-1.5 bg-gray-50 rounded-md text-sm text-gray-700">
              {job.lead.propertyAddress}
            </div>
          </div>

          <div className="space-y-0">
            <Label className="text-xs">Assigned To</Label>
            <div className="px-2 py-1.5 bg-gray-50 rounded-md text-sm text-gray-700">
              {job.lead.assignedTo || 'Unassigned'}
            </div>
          </div>

          <div className="space-y-0">
            <Label className="text-xs">Commission %</Label>
            <div className="px-2 py-1.5 bg-gray-50 rounded-md text-sm text-gray-700">
              {job.lead.commissionPercent}%
            </div>
          </div>

          {job.lead.insuranceCompany && (
            <>
              <div className="space-y-0">
                <Label className="text-xs">Insurance Company</Label>
                <div className="px-2 py-1.5 bg-gray-50 rounded-md text-sm text-gray-700">
                  {job.lead.insuranceCompany}
                </div>
              </div>

              {job.lead.claimNumber && (
                <div className="space-y-0">
                  <Label className="text-xs">Claim Number</Label>
                  <div className="px-2 py-1.5 bg-gray-50 rounded-md text-sm text-gray-700">
                    {job.lead.claimNumber}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Financial Summary */}
      <JobFinancialSummary financial={job.financial} />

      {/* Job Details - Editable Fields */}
      <div className="bg-white p-1.5 rounded-[20px] border border-[#d9d9d9]">
        <h3 className="text-[#0A0A9E] font-semibold text-sm mb-0.5">Job Details</h3>

        <div className="space-y-0.5">
          <div className="space-y-0">
            <Label htmlFor="description" className="text-xs">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
              className="text-sm min-h-[60px]"
              placeholder="Enter job description..."
            />
          </div>

          <div className="space-y-0">
            <Label htmlFor="notes" className="text-xs">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={2}
              className="text-sm min-h-[60px]"
              placeholder="Enter any notes..."
            />
          </div>

          {job.closingComments && (
            <div className="space-y-0">
              <Label className="text-xs">Closing Comments</Label>
              <div className="px-2 py-1.5 bg-gray-50 rounded-md text-xs text-gray-700 max-h-24 overflow-y-auto">
                {job.closingComments}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Button - Centered */}
      <div className="flex justify-center">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-[#D71F20] text-white hover:bg-[#D71F20]/90 rounded-[50px] px-12 py-2 text-sm font-medium"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
